import { createSlice } from "@reduxjs/toolkit";

import {
  fetchFees,
  fetchPaidFees,
  fetchStudentLastPaidFee,
  collectFee,
  updateFee,
  deleteFee,
  generateReceipt,
} from "./fees.thunk";

/* =========================================================
   INITIAL STATE
========================================================= */

const initialState = {
  /* ---------------- ALL FEES ---------------- */
  fees: [],
  totalPages: 1,
  currentPage: 1,
  total: 0,
  limit: 10,

  /* ---------------- PAID FEES ---------------- */
  paidFees: [],
  paidFeesCurrentPage: 1,
  paidFeesTotalPages: 1,
  paidFeesTotalRecords: 0,
  paidFeesSearch: "",

  /* ---------------- COMMON ---------------- */
  loading: false,
  error: null,
  selectedFee: null,
  lastPaidFee: null,
};

/* =========================================================
   SLICE
========================================================= */

const feesSlice = createSlice({
  name: "fees",

  initialState,

  reducers: {
    /* ---------------- ALL FEES ---------------- */

    setFeesCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    /* ---------------- PAID FEES ---------------- */

    setPaidFeesCurrentPage: (state, action) => {
      state.paidFeesCurrentPage = action.payload;
    },

    setPaidFeesSearch: (state, action) => {
      state.paidFeesSearch = action.payload;
    },

    /* ---------------- SELECTED FEE ---------------- */

    setSelectedFee: (state, action) => {
      state.selectedFee = action.payload;
    },

    clearSelectedFee: (state) => {
      state.selectedFee = null;
    },

    /* ---------------- LAST PAID FEE ---------------- */

    clearLastPaidFee: (state) => {
      state.lastPaidFee = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* =========================================================
         FETCH ALL FEES
      ========================================================= */

      .addCase(fetchFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchFees.fulfilled, (state, action) => {
        state.loading = false;

        state.fees = action.payload.data || [];

        state.totalPages =
          action.payload.meta?.total_pages || 1;

        state.total =
          action.payload.meta?.total || 0;
      })

      .addCase(fetchFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================================================
         FETCH PAID FEES
      ========================================================= */

      .addCase(fetchPaidFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchPaidFees.fulfilled, (state, action) => {
        state.loading = false;

        state.paidFees =
          action.payload.data || [];

        state.paidFeesCurrentPage =
          action.payload.pagination?.current_page || 1;

        state.paidFeesTotalPages =
          action.payload.pagination?.total_pages || 1;

        state.paidFeesTotalRecords =
          action.payload.pagination?.total_records || 0;

        state.paidFeesSearch =
          action.payload.filters?.search || "";
      })

      .addCase(fetchPaidFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================================================
         FETCH STUDENT LAST PAID FEE
      ========================================================= */

      .addCase(fetchStudentLastPaidFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchStudentLastPaidFee.fulfilled, (state, action) => {
        state.loading = false;
        state.lastPaidFee = action.payload;
      })

      .addCase(fetchStudentLastPaidFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.lastPaidFee = null;
      })

      /* =========================================================
         COLLECT FEE
      ========================================================= */

      .addCase(collectFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(collectFee.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(collectFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================================================
         UPDATE FEE
      ========================================================= */

      .addCase(updateFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateFee.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(updateFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================================================
         DELETE FEE
      ========================================================= */

      .addCase(deleteFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(deleteFee.fulfilled, (state, action) => {
        state.loading = false;

        state.fees = state.fees.filter(
          (fee) => fee.id !== action.payload.id
        );

        state.paidFees = state.paidFees.filter(
          (fee) => fee.id !== action.payload.id
        );
      })

      .addCase(deleteFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* =========================================================
         GENERATE RECEIPT
      ========================================================= */

      .addCase(generateReceipt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(generateReceipt.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(generateReceipt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

/* =========================================================
   EXPORT ACTIONS
========================================================= */

export const {
  setFeesCurrentPage,
  setPaidFeesCurrentPage,
  setPaidFeesSearch,
  setSelectedFee,
  clearSelectedFee,
  clearLastPaidFee,
} = feesSlice.actions;

/* =========================================================
   EXPORT REDUCER
========================================================= */

export default feesSlice.reducer;